// src/pages/dashboard/admin/users/AssignAgentModal.jsx
import { useState, useEffect } from 'react';
import api from '../../../../lib/api';
import { X } from 'lucide-react';
import FormSelect from '../../../../components/forms/FormSelect';
import Button from '../../../../components/layout/ui/Button';
import { success, error } from '../../../../components/common/Toast';

export default function AssignAgentModal({ nanny, isOpen, onClose, onSuccess }) {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingAgents, setFetchingAgents] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFetchingAgents(true);
      api
        .get('/admin/users?role=agent')
        .then(res => {
          const agentList = (res.data.data.data || []).map(agent => ({
            value: agent.id,
            label: `${agent.first_name} ${agent.last_name}`,
            subtitle: agent.phone_number,
            profile_picture: agent.profile_picture,
          }));
          setAgents(agentList);
        })
        .catch(() => error('Failed to load agents'))
        .finally(() => setFetchingAgents(false));
    }
  }, [isOpen]);

  const assign = async () => {
    if (!selectedAgent) return error('Please select an agent');
    setLoading(true);
    try {
      await api.post('/admin/assign-nanny', {
        nanny_id: nanny.id,
        agent_id: selectedAgent.value,
      });
      success('Nanny assigned successfully!');
      onSuccess();
      onClose();
    } catch {
      error('Failed to assign agent');
    } finally {
      setLoading(false);
    }
  };

  const customOption = (option) => (
    <div className="flex items-center gap-4 py-2">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-lg overflow-hidden">
        {option.profile_picture && !option.profile_picture.includes('default-avatar') ? (
          <img
            src={process.env.REACT_APP_IMAGE_URL + option.profile_picture}
            alt={option.label}
            className="w-full h-full object-cover"
          />
        ) : (
          option.label[0]
        )}
      </div>
      <div>
        <p className="font-semibold">{option.label}</p>
        <p className="text-sm text-gray-500">{option.subtitle}</p>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full p-10" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-4xl font-black">
            Assign Agent to <span className="text-primary">{nanny.first_name}</span>
          </h2>
          <button onClick={onClose} className="p-3 hover:bg-gray-100 rounded-xl transition">
            <X size={32} />
          </button>
        </div>

        {/* Searchable Agent Select */}
        <div className="mb-10">
          <FormSelect
            label="Select Agent"
            options={agents}
            value={selectedAgent}
            onChange={setSelectedAgent}
            placeholder="Search agents by name or phone..."
            loading={fetchingAgents}
            isSearchable={true}
            optionRender={customOption}
            required
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={onClose} className="px-10 py-5">
            Cancel
          </Button>
          <Button
            onClick={assign}
            loading={loading}
            disabled={!selectedAgent || loading}
            className="px-12 py-5 text-xl"
          >
            Assign Agent
          </Button>
        </div>
      </div>
    </div>
  );
}